import { cn } from "../../lib/utils"
import { Card, CardContent, CardHeader, CardTitle } from "./card"
import { useState, useRef, useEffect } from "react"
import { ChevronLeft, ChevronRight, Play, Pause, Maximize2, Download } from "lucide-react"

interface ExerciseCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  description?: string
  variations?: string[]
  className?: string
}

const ExerciseCard = ({
  className,
  title,
  description,
  variations = [],
  ...props
}: ExerciseCardProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [slideOffset, setSlideOffset] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const startX = useRef<number>(0)
  const isDragging = useRef(false)
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null)
  const slideThreshold = 100 // pixels to trigger slide
  const [isFullscreen, setIsFullscreen] = useState(false)

  const isVideo = (url: string) => {
    return url.endsWith('.mp4') || url.includes('video')
  }

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const toggleFullScreen = async () => {
    try {
      if (videoRef.current) {
        const wasPlaying = !videoRef.current.paused
        
        if (document.fullscreenElement) {
          await document.exitFullscreen()
          setIsFullscreen(false)
        } else {
          try {
            await videoRef.current.requestFullscreen()
            setIsFullscreen(true)
          } catch {
            const video = videoRef.current as HTMLVideoElement & { webkitEnterFullscreen?: () => void }
            if (video.webkitEnterFullscreen) {
              video.webkitEnterFullscreen()
              setIsFullscreen(true)
            } else {
              console.error('Fullscreen not supported')
            }
          }
        }

        if (wasPlaying) {
          videoRef.current.play()
        }
      }
    } catch (err) {
      console.error('Fullscreen error:', err)
    }
  }

  const nextVariation = () => {
    setCurrentIndex((prev) => (prev + 1) % variations.length)
  }

  const prevVariation = () => {
    setCurrentIndex((prev) => (prev - 1 + variations.length) % variations.length)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true
    startX.current = e.clientX
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return
    e.preventDefault()

    const deltaX = e.clientX - startX.current
    setSlideOffset(deltaX)
    
    if (deltaX > 0) {
      setSlideDirection('right')
    } else {
      setSlideDirection('left')
    }
  }

  const handleMouseUp = () => {
    if (Math.abs(slideOffset) > slideThreshold) {
      if (slideDirection === 'right') {
        prevVariation()
      } else {
        nextVariation()
      }
    }
    isDragging.current = false
    setSlideOffset(0)
    setSlideDirection(null)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    isDragging.current = true
    startX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return
    e.preventDefault()

    const deltaX = e.touches[0].clientX - startX.current
    setSlideOffset(deltaX)
    
    if (deltaX > 0) {
      setSlideDirection('right')
    } else {
      setSlideDirection('left')
    }
  }

  const handleTouchEnd = () => {
    if (Math.abs(slideOffset) > slideThreshold) {
      if (slideDirection === 'right') {
        prevVariation()
      } else {
        nextVariation()
      }
    }
    isDragging.current = false
    setSlideOffset(0)
    setSlideDirection(null)
  }

  const handleDotClick = (index: number) => {
    setCurrentIndex(index)
  }

  const handleDotsMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true
    startX.current = e.clientX
  }

  const handleDotsMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return

    const dotsContainer = e.currentTarget
    const containerRect = dotsContainer.getBoundingClientRect()
    const dotWidth = containerRect.width / variations.length
    const relativeX = e.clientX - containerRect.left
    const newIndex = Math.min(
      Math.max(Math.floor(relativeX / dotWidth), 0),
      variations.length - 1
    )
    
    setCurrentIndex(newIndex)
  }

  const handleDotsTouchStart = (e: React.TouchEvent) => {
    isDragging.current = true
    startX.current = e.touches[0].clientX
  }

  const handleDotsTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return

    const dotsContainer = e.currentTarget
    const containerRect = dotsContainer.getBoundingClientRect()
    const dotWidth = containerRect.width / variations.length
    const relativeX = e.touches[0].clientX - containerRect.left
    const newIndex = Math.min(
      Math.max(Math.floor(relativeX / dotWidth), 0),
      variations.length - 1
    )
    
    setCurrentIndex(newIndex)
  }

  const handleDotsTouchEnd = () => {
    isDragging.current = false
  }

  const handleVideoLoad = () => {
    setTimeout(() => {
      setIsLoading(false)
    }, 100)
  }

  const handleVideoError = () => {
    setIsLoading(false)
    // Could add error state handling here
  }

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    const url = variations[currentIndex]
    const filename = url.split('/').pop() || 'video.mp4'

    try {
      const response = await fetch(url)
      const blob = await response.blob()
      
      // For iOS Safari
      if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
        // Create a temporary link that opens in a new tab
        const blobUrl = window.URL.createObjectURL(blob)
        window.location.href = blobUrl
        // Clean up after navigation starts
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl)
        }, 100)
      } else {
        // For other browsers
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }
    } catch (error) {
      console.error('Download failed:', error)
    }
  }

  useEffect(() => {
    if (isVideo(variations[currentIndex])) {
      setIsLoading(true)
      
      const timeoutId = setTimeout(() => {
        setIsLoading(false)
      }, 5000)

      return () => clearTimeout(timeoutId)
    }
  }, [currentIndex, variations])

  useEffect(() => {
    const handleTouchMoveGlobal = (e: TouchEvent) => {
      if (isDragging.current) {
        e.preventDefault()
      }
    }

    document.addEventListener('touchmove', handleTouchMoveGlobal, { passive: false })
    return () => {
      document.removeEventListener('touchmove', handleTouchMoveGlobal)
    }
  }, [])

  useEffect(() => {
    const handleMouseMoveGlobal = (e: MouseEvent) => {
      if (!isDragging.current) return
      e.preventDefault()

      const deltaX = e.clientX - startX.current
      setSlideOffset(deltaX)
      
      if (deltaX > 0) {
        setSlideDirection('right')
      } else {
        setSlideDirection('left')
      }
    }

    const handleMouseUpGlobal = () => {
      if (Math.abs(slideOffset) > slideThreshold) {
        if (slideDirection === 'right') {
          prevVariation()
        } else {
          nextVariation()
        }
      }
      isDragging.current = false
      setSlideOffset(0)
      setSlideDirection(null)
    }

    document.addEventListener('mousemove', handleMouseMoveGlobal)
    document.addEventListener('mouseup', handleMouseUpGlobal)

    return () => {
      document.removeEventListener('mousemove', handleMouseMoveGlobal)
      document.removeEventListener('mouseup', handleMouseUpGlobal)
    }
  }, [slideOffset, slideDirection, slideThreshold])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
    }
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const showLeftArrow = currentIndex > 0
  const showRightArrow = currentIndex < variations.length - 1

  const handlePrevClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (currentIndex > 0) {
      prevVariation()
    }
  }

  const handleNextClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (currentIndex < variations.length - 1) {
      nextVariation()
    }
  }

  return (
    <Card
      className={cn(
        "bg-transparent backdrop-blur-none border-foreground relative aspect-[9/16] overflow-hidden",
        className
      )}
      {...props}
    >
      {variations.length > 0 && (
        <div 
          ref={containerRef}
          className="absolute inset-0 w-full h-full cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {variations.length > 1 && (
            <>
              <button
                onClick={handlePrevClick}
                className={cn(
                  "absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white transition-all duration-200",
                  showLeftArrow 
                    ? "opacity-100 translate-x-0" 
                    : "opacity-0 -translate-x-4 pointer-events-none"
                )}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={handleNextClick}
                className={cn(
                  "absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white transition-all duration-200",
                  showRightArrow 
                    ? "opacity-100 translate-x-0" 
                    : "opacity-0 translate-x-4 pointer-events-none"
                )}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          {isVideo(variations[currentIndex]) ? (
            <>
              <video
                ref={videoRef}
                key={variations[currentIndex]}
                src={variations[currentIndex]}
                className={cn(
                  "absolute inset-0 w-full h-full object-contain bg-black transition-opacity duration-300",
                  isLoading ? "opacity-0" : "opacity-100",
                  isFullscreen ? "pointer-events-auto" : "pointer-events-none"
                )}
                style={{
                  transform: `translateX(${slideOffset}px)`,
                  transition: isDragging.current 
                    ? 'none' 
                    : 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                autoPlay={isPlaying}
                loop
                muted
                playsInline
                controls={isFullscreen}
                onLoadedData={handleVideoLoad}
                onError={handleVideoError}
              />
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black">
                  <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                </div>
              )}
            </>
          ) : (
            <img
              key={variations[currentIndex]}
              src={variations[currentIndex]}
              alt={`${title || "Exercise"} variation ${currentIndex + 1}`}
              className="absolute inset-0 w-full h-full object-contain bg-black pointer-events-none"
              style={{
                transform: `translateX(${slideOffset}px)`,
                transition: isDragging.current 
                  ? 'none' 
                  : 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
          )}
        </div>
      )}
      
      <div className="relative z-10 h-full flex flex-col">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/70 pointer-events-none" />
        
        <CardHeader className="p-4 relative">
          {title && (
            <CardTitle className="text-xl text-white">
              {title}
            </CardTitle>
          )}
        </CardHeader>
        
        <CardContent className="p-4 pt-0 mt-auto relative">
          {description && (
            <p className="text-sm text-white/90">
              {description}
            </p>
          )}
          
          <div className="flex items-center justify-between mt-4">
            {isVideo(variations[currentIndex]) && (
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    togglePlay()
                  }}
                  className="text-white p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  {isPlaying ? (
                    <Pause size={16} />
                  ) : (
                    <Play size={16} />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleFullScreen()
                  }}
                  className="text-white p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <Maximize2 size={16} />
                </button>
                <button
                  onClick={handleDownload}
                  className="text-white p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <Download size={16} />
                </button>
              </div>
            )}
            
            {variations.length > 1 && (
              <div 
                className="flex justify-center gap-4 p-2 cursor-pointer touch-none"
                onMouseDown={handleDotsMouseDown}
                onMouseMove={handleDotsMouseMove}
                onMouseUp={() => isDragging.current = false}
                onMouseLeave={() => isDragging.current = false}
                onTouchStart={handleDotsTouchStart}
                onTouchMove={handleDotsTouchMove}
                onTouchEnd={handleDotsTouchEnd}
              >
                {variations.map((_, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      "w-4 h-4 md:w-3 md:h-3 rounded-full transition-all",
                      idx === currentIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/70"
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDotClick(idx)
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </div>
    </Card>
  )
}

export default ExerciseCard 